import { App, Button, Descriptions, Drawer, Input, Space, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "@/shared/hooks/usePermission";
import { useReviewAbsence } from "../hooks/useAbsences";
import { absencesService } from "../services/absences.service";
import { ABSENCE_STATUS_COLORS, type Absence } from "../types";

interface Props {
	absence: Absence | null;
	onClose: () => void;
}

/**
 * ⚠️ Tafsilot — DRAWER, alohida route EMAS. `routeTree.tsx` (poydevor)
 * faqat `/absences` ni e'lon qilgan (kontrakt §9); `/absences/$id`
 * qo'shish poydevor faylini o'zgartirish bo'lardi.
 */
export function AbsenceDetailDrawer({ absence, onClose }: Props) {
	const { t } = useTranslation("absences");
	const { message } = App.useApp();
	const { can } = usePermission();
	const review = useReviewAbsence();
	const [reviewNote, setReviewNote] = useState("");

	if (!absence) {
		return <Drawer open={false} onClose={onClose} />;
	}

	// ⚠️ Yuqoridagi early return dan keyingi torayish `qaror()` va
	// `hujjatniYuklash()` yopilmalari (closure) ichida SAQLANMASLIGI
	// mumkin — `absence` destrukturalangan PARAMETR, `const` emas.
	// Lokal `const` ga bog'lab, yopilmalarda AYNAN uni ishlating:
	// typecheck yiqilsa, sabab shu.
	const ariza = absence;
	const statusColor = ABSENCE_STATUS_COLORS[ariza.status];
	const kutilmoqda = ariza.status === "kutilmoqda";
	// ⚠️ `bekorQilsaBoladi` `kutilmoqda` ni O'Z ICHIGA OLADI — backend'ning
	// `KUTILGAN_STATUS.cancel = ["kutilmoqda", "tasdiqlangan"]` i bilan
	// bir xil. Pastda `kutilmoqda || bekorQilsaBoladi` YOZMANG: u
	// takroriy shart, `bekorQilsaBoladi` yolg'iz yetarli.
	const bekorQilsaBoladi = kutilmoqda || ariza.status === "tasdiqlangan";

	async function qaror(action: "approve" | "reject" | "cancel") {
		if (action === "reject" && reviewNote.trim().length === 0) {
			message.error(t("detailDrawer.rejectReasonRequired"));

			return;
		}

		await review.mutateAsync({ id: ariza.id, action, reviewNote: reviewNote || undefined });
		setReviewNote("");
		onClose();
	}

	async function hujjatniYuklash() {
		const blob = await absencesService.downloadDocument(ariza.id);
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = url;
		link.download = `ariza-${ariza.id}`;
		link.click();
		URL.revokeObjectURL(url);
	}

	return (
		<Drawer open width={520} title={t("detailDrawer.title")} onClose={onClose}>
			<Descriptions column={1} bordered size="small">
				<Descriptions.Item label={t("detailDrawer.typeLabel")}>
					{t(`type.${ariza.type}`)}
				</Descriptions.Item>
				<Descriptions.Item label={t("detailDrawer.statusLabel")}>
					<Tag color={statusColor}>{t(`status.${ariza.status}`)}</Tag>
				</Descriptions.Item>
				<Descriptions.Item label={t("detailDrawer.rangeLabel")}>
					{ariza.dateFrom} — {ariza.dateTo}
				</Descriptions.Item>
				<Descriptions.Item label={t("detailDrawer.reasonLabel")}>
					{ariza.reason ?? "—"}
				</Descriptions.Item>
				<Descriptions.Item label={t("detailDrawer.reviewNoteLabel")}>
					{ariza.reviewNote ?? "—"}
				</Descriptions.Item>
			</Descriptions>

			{/*
			  ⚠️ Hujjat tugmasi `absences:read_document` bilan gate qilinadi.
			  Bu FAQAT UX: haqiqiy qorovul backend'da — tugmani yashirish
			  ruxsatni bermaydi, faqat 403 ni ko'rsatmaydi.
			*/}
			{ariza.documentFileId && can("absences", "read_document") ? (
				<Button style={{ marginTop: 16 }} onClick={hujjatniYuklash}>
					{t("detailDrawer.downloadDocument")}
				</Button>
			) : null}

			{bekorQilsaBoladi ? (
				<Space direction="vertical" style={{ width: "100%", marginTop: 24 }}>
					<Input.TextArea
						rows={2}
						maxLength={2000}
						placeholder={t("detailDrawer.reviewNotePlaceholder")}
						value={reviewNote}
						onChange={(e) => setReviewNote(e.target.value)}
					/>
					<Space>
						{kutilmoqda && can("absences", "approve") ? (
							<Button type="primary" loading={review.isPending} onClick={() => qaror("approve")}>
								{t("detailDrawer.approve")}
							</Button>
						) : null}
						{kutilmoqda && can("absences", "approve") ? (
							<Button danger loading={review.isPending} onClick={() => qaror("reject")}>
								{t("detailDrawer.reject")}
							</Button>
						) : null}
						{bekorQilsaBoladi && can("absences", "update") ? (
							<Button loading={review.isPending} onClick={() => qaror("cancel")}>
								{t("detailDrawer.cancel")}
							</Button>
						) : null}
					</Space>
				</Space>
			) : null}
		</Drawer>
	);
}
